import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface FinancialTableEntry {
  category: string;
  value: string | number;
  _id?: string;
}

export interface BreakEvenCashFlow {
  labels: string[];
  data: number[];
}

export interface RevenueCostDataset {
  label: string;
  data: number[];
  _id?: string;
}

export interface RevenueCost {
  labels: string[];
  datasets: RevenueCostDataset[];
}

export interface CO2SavingsDataset {
  label: string;
  data: number[];
  _id?: string;
}

export interface CO2Savings {
  labels: string[];
  datasets: CO2SavingsDataset[];
}

export interface UtilityDetails {
  utilityId: string;
  utilityName: string;
  state: string;
  zipCode: string;
  scheduleId: string;
  totalCost: string | number;
}

export interface EnergyCosts {
  energy: number[];
  energyTime: number[];
  incrementalEnergy: number[];
  demand: number[];
  demandTime: number[];
  incrementalDemand: number[];
  serviceCharge: number[];
  otherCharges: number[];
  total: number[];
}

export interface GrantCostDetail {
  programName: string;
  creditAmount: string;
  fullAmountDetails?: string;
  appliesTo: string;
  eligibility: string;
  validUntil: string;
  actionLink: string;
  description: string;
  _id?: string;
}

export interface ReportData {
  reportName: string;
  location: string;
  netProfit: number;
  financialTable: FinancialTableEntry[];
  breakEvenCashFlow: BreakEvenCashFlow;
  revenueCost: RevenueCost;
  co2Savings: CO2Savings;
  utilization: number;
  utilityRate: number;
  timestamp: string;
  utilityDetails?: UtilityDetails;
  energyCosts?: EnergyCosts;
  grantCostDetails?: GrantCostDetail[];

  // Add tax benefits fields
  applyTaxBenefits?: boolean;
  businessTaxRate?: number;
  section179Limit?: number;
  bonusDepreciationPct?: number;
  qualifyingSpend?: number;
  taxSavings?: number;
  upfrontAfterTax?: number;

  disableUtilityEnergyInfo?: boolean;
}

export interface ReportResponse {
  reportName: string;
  location: string;
  netProfit: number;
  financialTable: FinancialTableEntry[];
  breakEvenCashFlow: BreakEvenCashFlow;
  revenueCost: RevenueCost;
  co2Savings: CO2Savings;
  utilization: number;
  utilityRate: number;
  timestamp: string;
  utilityDetails?: UtilityDetails;
  energyCosts?: EnergyCosts;
  grantCostDetails?: GrantCostDetail[];
  excludeIncentives?: boolean;

  // Add tax benefits fields
  applyTaxBenefits?: boolean;
  businessTaxRate?: number;
  section179Limit?: number;
  bonusDepreciationPct?: number;
  qualifyingSpend?: number;
  taxSavings?: number;
  upfrontAfterTax?: number;

  userId: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;

   disableUtilityEnergyInfo?: boolean;
}

export interface ReportApiResponse {
  success: boolean;
  message: string;
  data?: {
    report: ReportResponse;
  };
}

export interface ReportsApiResponse {
  success: boolean;
  message: string;
  data?: {
    reports: ReportResponse[];
    total: number;
    page: number;
    pages: number;
  };
}

export interface UpdateReportNameResponse {
  success: boolean;
  message: string;
  data?: {
    report: ReportResponse;
  };
}

export interface UtilityByZipCodeResponse {
  success: boolean;
  message: string;
  data?: {
    UtilityID: string;
    UtilityName: string;
    State: string;
  };
}

export interface Schedule {
  Pending: string;
  ScheduleID: string;
  UtilityID: string;
  ScheduleName: string;
  ScheduleDescription: string;
  UseType: string;
  MinDemand: string;
  MaxDemand: string;
  MinUsage: string;
  MaxUsage: string;
  EffectiveDate: string;
  OptionType: string;
  OptionDescription: string;
  UtilityName: string;
  State: string;
}

export interface ScheduleByUtilityResponse {
  success: boolean;
  message: string;
  data?: {
    Schedule: Schedule[];
  };
}

export interface EnergyByScheduleResponse {
  success: boolean;
  message: string;
  data?: {
    ratekWh: string;
  };
}

export interface ScheduleDetailTipData {
  Schedule_Table: Array<{
    Pending: string;
    ScheduleID: string;
    UtilityID: string;
    ScheduleName: string;
    ScheduleDescription: string;
    UseType: string;
    MinDemand: string;
    MaxDemand: string;
    MinUsage: string;
    MaxUsage: string;
    EffectiveDate: string;
    OptionType: string;
    OptionDescription: string;
    UtilityName: string;
    State: string;
  }>;
  DemandTime_Table: any[];
  IncrementalDemand_Table: any[];
  Demand_Table: Array<{
    Pending?: string;
    ScheduleID?: string;
    Description: string;
    RatekW: string;
    MinkV?: string;
    MaxkV?: string;
    Determinant: string;
    ChargeUnit: string;
  }>;
  EnergyTime_Table: Array<{
    Pending?: string;
    ScheduleID?: string;
    Description: string;
    RatekWh: string;
    MinkV?: string;
    MaxkV?: string;
    Season?: string;
    StartDate?: string;
    EndDate?: string;
    TimeOfDay?: string;
    StartTime?: string;
    EndTime?: string;
    MinTemp?: string;
    MaxTemp?: string;
    DaysAppDesc?: string;
  }>;
  IncrementalEnergy_Table: Array<{
    Description: string;
    RatekWh: string;
    StartkWh: string;
    EndkWh: string;
    Season: string;
  }>;
  Energy_Table: Array<{
    Pending?: string;
    ScheduleID?: string;
    Description: string;
    RatekWh: string;
    MinkV?: string;
    MaxkV?: string;
  }>;
  ServiceCharge_Table: Array<{
    Pending?: string;
    ScheduleID?: string;
    Description: string;
    Rate: string;
    ChargeUnit: string;
  }>;
  OtherCharges_Table: Array<{
    Pending?: string;
    ScheduleID?: string;
    ChargeType: string;
    Description: string;
    ChargeUnit: string;
  }>;
  ReactiveDemand_Table: any[];
  ModifiesSchedule_Table: any[];
  Notes_Table: Array<{
    Pending?: string;
    ScheduleID?: string;
    Description: string;
    Type: string;
  }>;
  TaxInfo_Table: any[];
  Percentages_Table: any[];
}

export interface ScheduleDetailTipResponse {
  success: boolean;
  message: string;
  data?: ScheduleDetailTipData;
}

export interface ApiUsagePerCustomer {
  userId: string;
  totalCalls: number;
  failures: number;
  failurePercentage: number;
  fullName: string;
}

export interface ApiUsageOverall {
  totalCalls: number;
  failures: number;
  failurePercentage: number;
}

export interface ApiUsageResponse {
  success: boolean;
  message: string;
  data?: {
    overall: ApiUsageOverall;
    perCustomer: ApiUsagePerCustomer[];
    total: number;
    page: number;
    pages: number;
  };
}

export interface RewiringAmericaIncentive {
  payment_methods: string[];
  authority_type: string;
  authority?: string;
  program: string;
  program_url: string;
  more_info_url?: string;
  items: string[];
  amount: {
    type: string;
    number: number;
    representative?: number;
    [key: string]: any;
  };
  owner_status: string[];
  start_date: string;
  end_date?: string;
  short_description: string;
}

export interface RewiringAmericaIncentivesResponse {
  success: boolean;
  message: string;
  data?: {
    is_under_80_ami: boolean;
    is_under_150_ami: boolean;
    is_over_150_ami: boolean;
    authorities: Record<string, { name: string }>;
    coverage: {
      state: string;
      utility: string | null;
    };
    location: {
      state: string;
    };
    data_partners: Record<string, any>;
    incentives: RewiringAmericaIncentive[];
  };
}

export interface UpdateFinancialTableRequest {
  id: string;
  netProfit: number;
  financialTable: FinancialTableEntry[];
}

export interface UpdateFinancialTableResponse {
  success: boolean;
  message: string;
  data?: {
    report: ReportResponse;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// SAVED GENERATED REPORT ACTION
export const savedGeneratedReport = createAsyncThunk<
  ReportApiResponse,
  ReportData,
  { rejectValue: string }
>("report/savedGeneratedReport", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<ReportApiResponse>(
      "/report",
      formData
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to create report");
      return rejectWithValue(
        response.data.message || "Failed to create report"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      const data = error.response.data as any;
      if (data.error && data.error.message) {
        errorMessage = data.error.message;
      } else {
        errorMessage = data.message || "Unknown error";
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE REPORT NAME ACTION
export const updateReportNameById = createAsyncThunk<
  UpdateReportNameResponse,
  { id: string; reportName: string },
  { rejectValue: string }
>(
  "report/updateReportNameById",
  async ({ id, reportName }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<UpdateReportNameResponse>(
        `/report/${id}`,
        { reportName }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Report name updated");
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to update report name");
        return rejectWithValue(
          response.data.message || "Failed to update report name"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as UpdateReportNameResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET PAST GENERATED REPORTS ACTION
export const getPastGeneratedReports = createAsyncThunk<
  ReportsApiResponse,
  {
    page?: number;
    limit?: number;
    formattedDateRange?: { startDate: string; endDate: string };
  },
  { rejectValue: string }
>(
  "report/getPastGeneratedReports",
  async ({ page, limit, formattedDateRange }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined) queryParams.append("page", page.toString());
      if (limit !== undefined) queryParams.append("limit", limit.toString());
      if (formattedDateRange?.startDate)
        queryParams.append("startDate", formattedDateRange.startDate);
      if (formattedDateRange?.endDate)
        queryParams.append("endDate", formattedDateRange.endDate);
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response = await createApiClient.get<ReportsApiResponse>(
        `/report${query}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve reports");
        return rejectWithValue(
          response.data.message || "Failed to retrieve reports"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ReportsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET SPECIFIC USER REPORTS
export const getSpecificUserReports = createAsyncThunk<
  ReportsApiResponse,
  { id: string; page?: number; limit?: number },
  { rejectValue: string }
>(
  "report/getSpecificUserReports",
  async ({ id, page, limit }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined) queryParams.append("page", page.toString());
      if (limit !== undefined) queryParams.append("limit", limit.toString());
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response = await createApiClient.get<ReportsApiResponse>(
        `/report/specific-user/${id}${query}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve reports");
        return rejectWithValue(
          response.data.message || "Failed to retrieve reports"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ReportsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET UTILITY BY ZIP CODE
export const getUtilityByZipCode = createAsyncThunk<
  UtilityByZipCodeResponse,
  string,
  { rejectValue: string }
>("report/getUtilityByZipCode", async (zipCode, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<UtilityByZipCodeResponse>(
      `/report/utility/${zipCode}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve utility data");
      return rejectWithValue(
        response.data.message || "Failed to retrieve utility data"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as UtilityByZipCodeResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET SCHEDULE BY UTILITY
export const getScheduleByUtility = createAsyncThunk<
  ScheduleByUtilityResponse,
  string,
  { rejectValue: string }
>("report/getScheduleByUtility", async (utilityId, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ScheduleByUtilityResponse>(
      `/report/schedule/${utilityId}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve schedule data");
      return rejectWithValue(
        response.data.message || "Failed to retrieve schedule data"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as ScheduleByUtilityResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ENERGY BY SCHEDULE
export const getEnergyBySchedule = createAsyncThunk<
  EnergyByScheduleResponse,
  string,
  { rejectValue: string }
>("report/getEnergyBySchedule", async (scheduleId, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<EnergyByScheduleResponse>(
      `/report/energy/${scheduleId}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve energy rate");
      return rejectWithValue(
        response.data.message || "Failed to retrieve energy rate"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as EnergyByScheduleResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET SCHEDULE DETAIL TIP
export const getScheduleDetailTip = createAsyncThunk<
  ScheduleDetailTipResponse,
  string,
  { rejectValue: string }
>("report/getScheduleDetailTip", async (scheduleId, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ScheduleDetailTipResponse>(
      `/report/schedule-detail/${scheduleId}`
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve schedule detail tip"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve schedule detail tip"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as ScheduleDetailTipResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET API USAGE REPORTS
export const getApiUsageReports = createAsyncThunk<
  ApiUsageResponse,
  {
    page?: number;
    limit?: number;
    formattedDateRange?: { startDate: string; endDate: string };
  },
  { rejectValue: string }
>(
  "report/getApiUsageReports",
  async ({ page, limit, formattedDateRange }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined) queryParams.append("page", page.toString());
      if (limit !== undefined) queryParams.append("limit", limit.toString());
      if (formattedDateRange?.startDate)
        queryParams.append("startDate", formattedDateRange.startDate);
      if (formattedDateRange?.endDate)
        queryParams.append("endDate", formattedDateRange.endDate);
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response = await createApiClient.get<ApiUsageResponse>(
        `/report/api-usage${query}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to retrieve API usage reports"
        );
        return rejectWithValue(
          response.data.message || "Failed to retrieve API usage reports"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as ApiUsageResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET REWIRING AMERICA INCENTIVES
export const getRewiringAmericaIncentives = createAsyncThunk<
  RewiringAmericaIncentivesResponse,
  {
    owner_status: string;
    household_income: number;
    household_size: number;
    zip: string;
  },
  { rejectValue: string }
>(
  "report/getRewiringAmericaIncentives",
  async (params, { rejectWithValue }) => {
    try {
      const items = [
        "ebike",
        "electric_vehicle_charger",
        "new_electric_vehicle",
        "new_plugin_hybrid_vehicle",
        "used_electric_vehicle",
        "used_plugin_hybrid_vehicle",
        "electric_panel",
        "electric_wiring",
        "electric_service_upgrades",
        "efficiency_rebates",
        "energy_audit",
      ].join(",");
      const queryParams = new URLSearchParams({
        owner_status: params.owner_status,
        household_income: params.household_income.toString(),
        household_size: params.household_size.toString(),
        zip: params.zip,
        items,
      });
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response =
        await createApiClient.get<RewiringAmericaIncentivesResponse>(
          `/report/incentives${query}`
        );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve incentives");
        return rejectWithValue(
          response.data.message || "Failed to retrieve incentives"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as RewiringAmericaIncentivesResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// UPDATE FINANCIAL TABLE ACTION
export const updateSiteReportData = createAsyncThunk<
  UpdateFinancialTableResponse,
  UpdateFinancialTableRequest,
  { rejectValue: string }
>(
  "report/updateSiteReportData",
  async ({ id, netProfit, financialTable }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<UpdateFinancialTableResponse>(
        `/report/financial-table/${id}`,
        { netProfit, financialTable }
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Financial table updated successfully"
        );
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to update financial table"
        );
        return rejectWithValue(
          response.data.message || "Failed to update financial table"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as UpdateFinancialTableResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
